/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useState, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../_mcp/helpers'
import {getCheckUserFunds} from './_requests'
import {UserFund, UserFundsQuery} from './_models'
import {useQueryRequest} from './QueryRequestProvider'

const QueryResponseContext = createResponseContext<UserFund>(initialQueryResponse)
const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state))
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state])

  const convertParamToObject = () => {
    const paramUrl: string = query;
    const paramToObject: any = JSON.parse('{"' + decodeURI(paramUrl).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g,'":"') + '"}')

    return paramToObject
  }

  const removeBlankAttributes = (query: UserFundsQuery) => {
    return Object.fromEntries(Object.entries(query).filter(([_, v]) => v != null));
  }

  const getObjectFilter = (query: any) => {
    const objectFilter: any = {};
    
    Object.keys(query).forEach(function(key){
      if (key.includes("filter_") && key !== "filter_batch") {
        const newKey = key.replace(/^filter_/, '')
        objectFilter[newKey] = query[key]
      }
    });

    return objectFilter;
  }

  const queryPayload = () => {
    const paramObj: any = convertParamToObject()
    const filterObj = getObjectFilter(paramObj)
    const page = paramObj.page
    const limit = paramObj.limit
    const sort = !!paramObj.sort ? paramObj.order === 'asc' ? `${paramObj.sort}` : `-${paramObj.sort}` : '-createdAt'
    const search = !!paramObj.search ? paramObj.search : null
    const sanitizedQuery: UserFundsQuery = {
      paging: {
        page: page,
        limit: limit,
        sort: sort,
      },
      searchData: search,
      ...filterObj
    }
    const newQuery = removeBlankAttributes(sanitizedQuery)
    
    return newQuery
  }

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.CHECK_USER_FUNDS_LIST}-${query}`,
    () => {
      const newQuery = queryPayload()
      return getCheckUserFunds(newQuery)
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false, refetchOnMount: true}
  )

  return (
    <QueryResponseContext.Provider value={{isLoading: isFetching, refetch, response, query}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = () => {
  const {response} = useQueryResponse()
  if (!response) {
    return []
  }

  return response.detailData.docs
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    ...initialQueryState,
  }

  const {response} = useQueryResponse()
  
  if (!response || !response.detailData) {
    return defaultPaginationState
  }

  const newResponse = {...response.detailData}

  delete newResponse.docs

  return newResponse
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}
